import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { L as Link } from "../lib/i18n";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import CarCard from "../components/CarCard";
import JsonLd from "../components/JsonLd";
import DealerReviews, { StarRow } from "../components/DealerReviews";
import { trackEvent } from "../lib/analytics";
import { useSeoHead } from "../lib/seoHead";
import {
  Building2, ShieldCheck, MapPin, Calendar, Phone, Globe, ArrowLeft, Loader2,
  MessageCircle, Clock, Facebook, Instagram, Linkedin, Youtube, Music2, Car, Crown, Award,
} from "lucide-react";

const SITE_URL = (process.env.REACT_APP_PUBLIC_URL || "").replace(/\/$/, "");
const WEEKDAY_LABELS = { Mo: "Montag", Di: "Dienstag", Mi: "Mittwoch", Do: "Donnerstag", Fr: "Freitag", Sa: "Samstag", So: "Sonntag" };
const WEEKDAY_ORDER = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const SCHEMA_DAYS = { Mo: "Monday", Di: "Tuesday", Mi: "Wednesday", Do: "Thursday", Fr: "Friday", Sa: "Saturday", So: "Sunday" };
// JS Date.getDay(): 0=Sun..6=Sat → mapped to our weekday keys
const JS_DAY_TO_KEY = { 0: "So", 1: "Mo", 2: "Di", 3: "Mi", 4: "Do", 5: "Fr", 6: "Sa" };

const SOCIAL_META = {
  facebook:  { Icon: Facebook,  label: "Facebook",  brand: "hover:bg-[#1877F2] hover:border-[#1877F2]" },
  instagram: { Icon: Instagram, label: "Instagram", brand: "hover:bg-gradient-to-tr hover:from-amber-500 hover:via-pink-500 hover:to-purple-600 hover:border-pink-500" },
  linkedin:  { Icon: Linkedin,  label: "LinkedIn",  brand: "hover:bg-[#0A66C2] hover:border-[#0A66C2]" },
  youtube:   { Icon: Youtube,   label: "YouTube",   brand: "hover:bg-[#FF0000] hover:border-[#FF0000]" },
  tiktok:    { Icon: Music2,    label: "TikTok",    brand: "hover:bg-[#25F4EE] hover:border-[#25F4EE] hover:text-black" },
};

function whatsappHref(phone) {
  if (!phone) return null;
  const digits = phone.replace(/[^\d]/g, "");
  return digits ? `https://wa.me/${digits}` : null;
}

/** Live „Jetzt offen" calculation against current local Swiss time. */
function computeOpenStatus(hours) {
  if (!Array.isArray(hours) || hours.length === 0) return null;
  const now = new Date();
  const todayKey = JS_DAY_TO_KEY[now.getDay()];
  const today = hours.find((h) => h.day === todayKey);
  const minutes = now.getHours() * 60 + now.getMinutes();
  const parse = (s) => {
    if (!s || !s.includes(":")) return null;
    const [h, m] = s.split(":").map(Number);
    return h * 60 + m;
  };
  if (today && !today.closed) {
    const o = parse(today.open);
    const c = parse(today.close);
    if (o !== null && c !== null && minutes >= o && minutes < c) {
      return { open: true, until: today.close, today };
    }
  }
  // Look ahead for next opening (up to 7 days)
  for (let i = 1; i <= 7; i++) {
    const key = JS_DAY_TO_KEY[(now.getDay() + i) % 7];
    const day = hours.find((h) => h.day === key);
    if (day && !day.closed) {
      return { open: false, nextDay: day.day, nextOpen: day.open, daysAhead: i };
    }
  }
  return { open: false };
}

function OpenStatusPill({ status }) {
  if (!status) return null;
  if (status.open) {
    return (
      <span
        data-testid="dealer-open-status"
        className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-300"
      >
        <span className="relative inline-flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
        </span>
        Jetzt offen{status.until ? ` · bis ${status.until}` : ""}
      </span>
    );
  }
  return (
    <span
      data-testid="dealer-open-status"
      className="inline-flex items-center gap-2 rounded-full border border-zinc-700 bg-zinc-900/60 px-3 py-1 text-xs font-semibold text-zinc-400"
    >
      <span className="inline-block h-2 w-2 rounded-full bg-zinc-500" />
      Geschlossen{status.nextDay && status.nextOpen ? ` · öffnet ${WEEKDAY_LABELS[status.nextDay] || status.nextDay} ${status.nextOpen}` : ""}
    </span>
  );
}

export default function DealerDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  // Phase 60 — live aggregate so JSON-LD + hero pill stay in sync after writes
  const [aggregate, setAggregate] = useState({ average: 0, count: 0 });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    api.getDealer(slug)
      .then((d) => {
        if (!alive) return;
        setData(d);
        setAggregate({ average: d.rating_avg || 0, count: d.rating_count || 0 });
        // Phase 64 — analytics
        trackEvent("dealer_view", {
          dealer_id: d.id,
          dealer_slug: slug,
          listings_count: d.listings_count || 0,
          rating_avg: d.rating_avg || 0,
          rating_count: d.rating_count || 0,
        });
      })
      .catch((err) => alive && setError(err?.response?.status === 404 ? "Händler nicht gefunden" : "Fehler beim Laden"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);

  const openStatus = useMemo(() => computeOpenStatus(data?.hours), [data?.hours]);
  const orderedHours = useMemo(() => {
    if (!Array.isArray(data?.hours)) return [];
    const byDay = Object.fromEntries(data.hours.map((h) => [h.day, h]));
    return WEEKDAY_ORDER.map((d) => byDay[d]).filter(Boolean);
  }, [data?.hours]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-zinc-500">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <Card className="border-zinc-800 bg-zinc-950 p-8 text-center text-white" data-testid="dealer-not-found">
          <Building2 className="mx-auto h-10 w-10 text-zinc-700" />
          <h1 className="mt-3 font-display text-2xl font-bold">{error || "Nicht gefunden"}</h1>
          <Button asChild className="mt-6 bg-red-600 hover:bg-red-500">
            <Link to="/dealers"><ArrowLeft className="mr-2 h-4 w-4" /> Alle Händler</Link>
          </Button>
        </Card>
      </div>
    );
  }

  const wa = whatsappHref(data.whatsapp);
  const todayKey = JS_DAY_TO_KEY[new Date().getDay()];

  return (
    <div className="pb-24 lg:pb-12" data-testid="dealer-detail">
      <DealerSeo data={data} slug={slug} aggregate={aggregate} />

      {/* PREMIUM BANNER-HERO */}
      <section className="relative">
        {data.banner_url ? (
          <div className="relative h-56 w-full overflow-hidden sm:h-72 lg:h-96" data-testid="dealer-banner">
            <img src={data.banner_url} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent" />
          </div>
        ) : (
          <div className="relative h-32 w-full overflow-hidden bg-gradient-to-br from-zinc-950 via-black to-zinc-900 sm:h-40">
            <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, rgba(220,38,38,0.25), transparent 50%)" }} />
          </div>
        )}

        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="-mt-20 sm:-mt-24">
            <Link to="/dealers" className="mb-4 inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-red-400">
              <ArrowLeft className="h-3.5 w-3.5" /> Alle Händler
            </Link>

            <div className="flex flex-col gap-5 rounded-2xl border border-zinc-800 bg-zinc-950/90 p-5 backdrop-blur-md sm:p-7 sm:flex-row sm:items-end">
              {/* Logo */}
              <div className="flex-shrink-0">
                {data.logo_url ? (
                  <img
                    src={data.logo_url}
                    alt={data.company_name}
                    className="h-24 w-24 rounded-2xl object-cover ring-4 ring-black/80 shadow-2xl sm:h-28 sm:w-28"
                  />
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-gradient-to-br from-red-600 to-red-800 ring-4 ring-black/80 shadow-2xl sm:h-28 sm:w-28">
                    <Building2 className="h-12 w-12 text-white" />
                  </div>
                )}
              </div>

              {/* Title block */}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-display text-3xl font-bold leading-tight text-white sm:text-4xl lg:text-5xl" data-testid="dealer-detail-name">
                    {data.company_name}
                  </h1>
                  {data.dealer_verified && data.verification_level === "premium" ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-500 to-amber-300 px-3 py-1 text-xs font-bold uppercase tracking-wider text-black ring-1 ring-amber-300/40 shadow-lg"
                      data-testid="dealer-verified-badge"
                      data-level="premium"
                      title="Premium-verifizierter Händler"
                    >
                      <Crown className="h-3.5 w-3.5" /> Premium Verified
                    </span>
                  ) : data.dealer_verified ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-red-600/20 px-3 py-1 text-xs font-bold uppercase tracking-wider text-red-300 ring-1 ring-red-500/40"
                      data-testid="dealer-verified-badge"
                      data-level="standard"
                    >
                      <ShieldCheck className="h-3.5 w-3.5" /> Verifiziert
                    </span>
                  ) : null}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-zinc-400">
                  {(data.address || data.canton) && (
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" />
                      {data.address || data.canton}
                    </span>
                  )}
                  {data.since_year && (
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" /> seit {data.since_year}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1.5">
                    <Car className="h-3.5 w-3.5" /> {data.listings_count || 0} aktive Inserate
                  </span>
                  {aggregate.count > 0 && (
                    <a
                      href="#reviews"
                      className="inline-flex items-center gap-1.5 text-zinc-300 hover:text-amber-300"
                      data-testid="hero-rating-pill"
                    >
                      <StarRow value={aggregate.average} size={14} />
                      <span className="font-semibold text-white">{aggregate.average.toFixed(1)}</span>
                      <span>({aggregate.count})</span>
                    </a>
                  )}
                </div>

                {openStatus && (
                  <div className="mt-4">
                    <OpenStatusPill status={openStatus} />
                  </div>
                )}
              </div>
            </div>

            {/* Stat strip */}
            <div className="mt-4 grid grid-cols-3 gap-3 sm:gap-4">
              <StatTile label="Inserate" value={data.listings_count || 0} icon={Car} />
              <StatTile label="Seit" value={data.since_year || "—"} icon={Calendar} />
              <StatTile label="Standort" value={data.canton || "CH"} icon={MapPin} />
            </div>

            {/* Desktop CTA bar */}
            <div className="mt-6 hidden flex-wrap gap-2 lg:flex" data-testid="dealer-cta-bar-desktop">
              {wa && (
                <Button asChild size="lg" className="bg-emerald-600 text-white shadow-lg shadow-emerald-900/30 hover:bg-emerald-500" data-testid="dealer-whatsapp-cta">
                  <a
                    href={wa}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent("whatsapp_click", { dealer_id: data.id, dealer_slug: slug, source: "dealer_hero" })}
                  >
                    <MessageCircle className="mr-2 h-5 w-5" /> WhatsApp
                  </a>
                </Button>
              )}
              {data.phone && (
                <Button asChild size="lg" className="bg-red-600 text-white shadow-lg shadow-red-900/30 hover:bg-red-500" data-testid="dealer-phone-cta">
                  <a
                    href={`tel:${data.phone}`}
                    onClick={() => trackEvent("phone_click", { dealer_id: data.id, dealer_slug: slug, source: "dealer_hero" })}
                  >
                    <Phone className="mr-2 h-5 w-5" /> {data.phone}
                  </a>
                </Button>
              )}
              {data.website && (
                <Button asChild size="lg" variant="outline" className="border-zinc-700 bg-transparent text-white hover:bg-zinc-900" data-testid="dealer-website-cta">
                  <a href={data.website} target="_blank" rel="noopener noreferrer">
                    <Globe className="mr-2 h-5 w-5" /> Website
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT GRID */}
      <section className="mx-auto mt-8 max-w-6xl px-4 sm:px-6 lg:mt-12 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          {/* Left column — description + listings */}
          <div className="space-y-6">
            {data.description && (
              <Card className="border-zinc-800 bg-zinc-950 p-6">
                <h2 className="mb-3 font-display text-lg font-bold text-white">Über uns</h2>
                <p className="whitespace-pre-line text-sm leading-relaxed text-zinc-300" data-testid="dealer-description">
                  {data.description}
                </p>
              </Card>
            )}

            {/* Listings */}
            <div id="listings">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-2xl font-bold text-white">
                  Inserate <span className="text-zinc-500">({data.listings_count || 0})</span>
                </h2>
              </div>
              {data.listings && data.listings.length > 0 ? (
                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="dealer-listings-grid">
                  {data.listings.map((car) => (
                    <CarCard key={car.id} car={car} />
                  ))}
                </div>
              ) : (
                <Card className="mt-5 border-dashed border-zinc-800 bg-zinc-950 p-10 text-center">
                  <Car className="mx-auto h-10 w-10 text-zinc-700" />
                  <p className="mt-3 text-sm text-zinc-500">Aktuell keine aktiven Inserate.</p>
                </Card>
              )}
            </div>

            {/* Phase 60 — Reviews */}
            <div id="reviews">
              <DealerReviews
                slug={slug}
                dealerId={data.id}
                initialAverage={data.rating_avg || 0}
                initialCount={data.rating_count || 0}
                onAggregateChange={(avg, cnt) => setAggregate({ average: avg, count: cnt })}
              />
            </div>
          </div>

          {/* Right column — hours + socials + trust */}
          <aside className="space-y-4">
            {/* Hours */}
            {orderedHours.length > 0 && (
              <Card className="border-zinc-800 bg-zinc-950 p-5" data-testid="dealer-hours">
                <h3 className="mb-4 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-zinc-200">
                  <Clock className="h-4 w-4 text-red-500" /> Öffnungszeiten
                </h3>
                <dl className="space-y-1.5 text-sm">
                  {orderedHours.map((h) => {
                    const isToday = h.day === todayKey;
                    return (
                      <div
                        key={h.day}
                        className={`flex items-center justify-between rounded-md px-2 py-1 ${isToday ? "bg-red-500/5 ring-1 ring-red-500/30" : ""}`}
                        data-testid={`hours-row-${h.day}`}
                      >
                        <dt className={`text-xs font-semibold uppercase tracking-wider ${isToday ? "text-red-300" : "text-zinc-400"}`}>
                          {WEEKDAY_LABELS[h.day] || h.day}
                          {isToday && <span className="ml-1.5 text-[10px] font-bold text-red-400">· Heute</span>}
                        </dt>
                        <dd className={`text-sm tabular-nums ${h.closed ? "text-zinc-600" : isToday ? "text-white font-semibold" : "text-zinc-200"}`}>
                          {h.closed ? "Geschlossen" : `${h.open}–${h.close}`}
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              </Card>
            )}

            {/* Socials */}
            {data.socials && Object.values(data.socials).some(Boolean) && (
              <Card className="border-zinc-800 bg-zinc-950 p-5" data-testid="dealer-socials">
                <h3 className="mb-4 font-display text-sm font-bold uppercase tracking-wider text-zinc-200">Folge uns</h3>
                <ul className="flex flex-wrap gap-2">
                  {Object.entries(data.socials).filter(([_k, v]) => v).map(([k, v]) => {
                    const meta = SOCIAL_META[k];
                    if (!meta) return null;
                    const { Icon, label, brand } = meta;
                    return (
                      <li key={k}>
                        <a
                          href={v}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={label}
                          title={label}
                          className={`group inline-flex h-11 w-11 items-center justify-center rounded-xl border border-zinc-800 bg-zinc-900 text-zinc-300 transition-all duration-200 hover:scale-110 hover:text-white ${brand}`}
                          data-testid={`social-${k}`}
                        >
                          <Icon className="h-5 w-5" />
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </Card>
            )}

            {/* Trust card */}
            {data.dealer_verified && (
              <Card
                className={`p-5 ${data.verification_level === "premium" ? "border-amber-400/40 bg-gradient-to-br from-amber-500/10 via-amber-400/5 to-transparent" : "border-emerald-500/30 bg-emerald-500/5"}`}
                data-testid="dealer-trust-card"
                data-level={data.verification_level || "standard"}
              >
                <div className="flex items-start gap-3">
                  {data.verification_level === "premium" ? (
                    <Crown className="h-6 w-6 flex-shrink-0 text-amber-300" />
                  ) : (
                    <ShieldCheck className="h-6 w-6 flex-shrink-0 text-emerald-400" />
                  )}
                  <div className="min-w-0">
                    <h4 className={`font-display text-sm font-bold ${data.verification_level === "premium" ? "text-amber-200" : "text-emerald-200"}`}>
                      {data.verification_level === "premium" ? "Premium Verifizierter Händler" : "Verifizierter Händler"}
                    </h4>
                    <p className={`mt-1 text-xs leading-relaxed ${data.verification_level === "premium" ? "text-amber-200/80" : "text-emerald-300/80"}`}>
                      CarHive hat Identität und Sitz dieses Händlers geprüft.
                    </p>
                    {/* Phase 65 — Trust meta rows */}
                    <ul className="mt-3 space-y-1.5 text-[11px] text-zinc-300">
                      {data.verified_at && (
                        <li className="inline-flex items-center gap-1.5" data-testid="trust-verified-at">
                          <Award className="h-3 w-3 text-zinc-500" />
                          Verifiziert seit {new Date(data.verified_at).toLocaleDateString("de-CH", { month: "long", year: "numeric" })}
                        </li>
                      )}
                      {data.member_since && (
                        <li className="inline-flex items-center gap-1.5" data-testid="trust-member-since">
                          <Calendar className="h-3 w-3 text-zinc-500" />
                          Mitglied seit {new Date(data.member_since).toLocaleDateString("de-CH", { month: "long", year: "numeric" })}
                        </li>
                      )}
                      {data.rating_count > 0 && (
                        <li className="inline-flex items-center gap-1.5" data-testid="trust-rating-summary">
                          <ShieldCheck className="h-3 w-3 text-zinc-500" />
                          {data.rating_count} {data.rating_count === 1 ? "Bewertung" : "Bewertungen"} · ⌀ {data.rating_avg.toFixed(1)}
                        </li>
                      )}
                      <li className="inline-flex items-center gap-1.5" data-testid="trust-listings-count">
                        <Car className="h-3 w-3 text-zinc-500" />
                        {data.listings_count || 0} aktive Inserate
                      </li>
                    </ul>
                  </div>
                </div>
              </Card>
            )}

            {/* Quick facts */}
            <Card className="border-zinc-800 bg-zinc-950 p-5">
              <h3 className="mb-4 font-display text-sm font-bold uppercase tracking-wider text-zinc-200">Kontakt</h3>
              <ul className="space-y-3 text-sm">
                {data.phone && (
                  <li>
                    <a href={`tel:${data.phone}`} className="inline-flex items-center gap-2 text-zinc-300 hover:text-red-300" data-testid="dealer-phone-link">
                      <Phone className="h-4 w-4 text-red-500" /> {data.phone}
                    </a>
                  </li>
                )}
                {wa && (
                  <li>
                    <a href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-zinc-300 hover:text-emerald-300" data-testid="dealer-whatsapp-link">
                      <MessageCircle className="h-4 w-4 text-emerald-500" /> WhatsApp
                    </a>
                  </li>
                )}
                {data.website && (
                  <li>
                    <a href={data.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-zinc-300 hover:text-blue-300" data-testid="dealer-website-link">
                      <Globe className="h-4 w-4 text-blue-400" /> {data.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                    </a>
                  </li>
                )}
                {data.address && (
                  <li className="flex items-start gap-2 text-zinc-300">
                    <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-zinc-500" />
                    <span>{data.address}{data.canton ? `, ${data.canton}` : ""}</span>
                  </li>
                )}
              </ul>
            </Card>
          </aside>
        </div>
      </section>

      {/* MOBILE STICKY CTA BAR */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-zinc-800 bg-black/95 px-3 py-3 backdrop-blur-md lg:hidden" data-testid="dealer-mobile-cta-bar">
        <div className="mx-auto flex max-w-md gap-2">
          {wa && (
            <Button asChild className="flex-1 bg-emerald-600 text-white hover:bg-emerald-500" data-testid="dealer-mobile-whatsapp">
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("whatsapp_click", { dealer_id: data.id, dealer_slug: slug, source: "dealer_mobile_bar" })}
              >
                <MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp
              </a>
            </Button>
          )}
          {data.phone && (
            <Button asChild className="flex-1 bg-red-600 text-white hover:bg-red-500" data-testid="dealer-mobile-call">
              <a
                href={`tel:${data.phone}`}
                onClick={() => trackEvent("phone_click", { dealer_id: data.id, dealer_slug: slug, source: "dealer_mobile_bar" })}
              >
                <Phone className="mr-1.5 h-4 w-4" /> Anrufen
              </a>
            </Button>
          )}
          {!data.phone && !wa && data.website && (
            <Button asChild className="flex-1 bg-red-600 text-white hover:bg-red-500">
              <a href={data.website} target="_blank" rel="noopener noreferrer"><Globe className="mr-1.5 h-4 w-4" /> Website</a>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/70 px-3 py-3 sm:px-4 sm:py-4" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mt-1 font-display text-xl font-bold text-white sm:text-2xl">{value}</div>
    </div>
  );
}

/**
 * SEO head + JSON-LD AutoDealer schema for the public dealer page.
 * Phase 60 — adds aggregateRating + reviews when present.
 */
function DealerSeo({ data, slug, aggregate }) {
  const origin = SITE_URL || (typeof window !== "undefined" ? window.location.origin : "");
  const title = `${data.company_name || "Händler"} · CarHive`;
  const desc = data.description
    ? data.description.replace(/\s+/g, " ").slice(0, 160)
    : `Inserate von ${data.company_name || "diesem Händler"} auf CarHive — Schweizer Auto-Marktplatz.`;
  const image = data.banner_url || data.logo_url || `${origin}/icons/og-default.png`;
  useSeoHead({
    title,
    description: desc,
    canonical: `${origin}/de/dealers/${slug}`,
    image,
    ogType: "profile",
    hreflangs: {
      de: `${origin}/de/dealers/${slug}`,
      fr: `${origin}/fr/dealers/${slug}`,
      it: `${origin}/it/dealers/${slug}`,
      en: `${origin}/en/dealers/${slug}`,
      "x-default": `${origin}/de/dealers/${slug}`,
    },
  });

  const ratingCount = aggregate?.count ?? data.rating_count ?? 0;
  const ratingValue = aggregate?.average ?? data.rating_avg ?? 0;
  const previewReviews = Array.isArray(data.reviews_preview) ? data.reviews_preview : [];

  const dealerSchema = {
    "@context": "https://schema.org",
    "@type": "AutoDealer",
    "@id": `${origin}/de/dealers/${slug}`,
    name: data.company_name,
    description: data.description || undefined,
    url: `${origin}/de/dealers/${slug}`,
    image: image || undefined,
    logo: data.logo_url || undefined,
    telephone: data.phone || undefined,
    foundingDate: data.since_year ? String(data.since_year) : undefined,
    address: data.address ? {
      "@type": "PostalAddress",
      streetAddress: data.address,
      addressRegion: data.canton || undefined,
      addressCountry: "CH",
    } : undefined,
    sameAs: data.socials ? Object.values(data.socials).filter(Boolean) : undefined,
    openingHoursSpecification: Array.isArray(data.hours)
      ? data.hours.filter((h) => !h.closed && SCHEMA_DAYS[h.day]).map((h) => ({
          "@type": "OpeningHoursSpecification",
          dayOfWeek: SCHEMA_DAYS[h.day],
          opens: h.open,
          closes: h.close,
        }))
      : undefined,
    aggregateRating: ratingCount > 0 ? {
      "@type": "AggregateRating",
      ratingValue: Number(ratingValue).toFixed(1),
      reviewCount: ratingCount,
      bestRating: "5",
      worstRating: "1",
    } : undefined,
    review: previewReviews.length > 0
      ? previewReviews.map((r) => ({
          "@type": "Review",
          author: { "@type": "Person", name: r.reviewer_name || "Anonym" },
          datePublished: r.created_at ? r.created_at.slice(0, 10) : undefined,
          reviewBody: r.comment || undefined,
          reviewRating: {
            "@type": "Rating",
            ratingValue: String(r.rating),
            bestRating: "5",
            worstRating: "1",
          },
        }))
      : undefined,
  };
  Object.keys(dealerSchema).forEach((k) => dealerSchema[k] === undefined && delete dealerSchema[k]);

  return <JsonLd id={`dealer-${slug}`} data={dealerSchema} />;
}
